/* Account Security — 2FA TOTP setup, hot desk PIN reset, Twilio settings link */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
    ShieldCheck, QrCode, CheckCircle, WarningCircle, FloppyDisk,
    Copy, KeyReturn,
} from '@phosphor-icons/react';

const TwoFactorAuth = () => {
    const [status, setStatus] = useState(null);
    const [setup, setSetup] = useState(null);       // {secret_b32, provisioning_uri, qr_png_base64}
    const [verifyCode, setVerifyCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [disableCode, setDisableCode] = useState('');

    const load = () => api.get('/auth/2fa/status').then((r) => setStatus(r.data));
    useEffect(() => { load(); }, []);

    const startSetup = async () => {
        try {
            const r = await api.post('/auth/2fa/setup');
            setSetup(r.data);
            setRecoveryCodes(null);
        } catch (e) {
            toast.error('Setup selhal: ' + (e.response?.data?.detail || e.message));
        }
    };

    const verify = async () => {
        try {
            const r = await api.post('/auth/2fa/verify', { code: verifyCode });
            setRecoveryCodes(r.data.recovery_codes);
            setSetup(null);
            setVerifyCode('');
            toast.success('2FA aktivováno!');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Neplatný kód');
        }
    };

    const disable = async () => {
        if (!disableCode) return toast.error('Zadejte kód');
        try {
            await api.post('/auth/2fa/disable', { code: disableCode });
            setDisableCode('');
            toast.success('2FA deaktivováno');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Chyba');
        }
    };

    const copy = (txt) => {
        navigator.clipboard.writeText(txt).then(() => toast.success('Zkopírováno'));
    };

    if (!status) return <div className="py-10 text-center">Načítám…</div>;

    return (
        <div className="max-w-2xl" data-testid="2fa-page">
            <div className="mb-6">
                <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                    <ShieldCheck size={28} style={{ color: 'var(--brand)' }} />
                    Dvoufaktorové ověření
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    TOTP (Google Authenticator, Authy, 1Password)
                </p>
            </div>

            <div className="rounded-xl border p-6 mb-4"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="flex items-center gap-2 mb-3">
                    {status.enabled ? (
                        <><CheckCircle size={18} weight="fill" color="#22c55e" />
                          <span className="text-sm font-bold" style={{ color: '#22c55e' }}
                                data-testid="2fa-status">Aktivní</span></>
                    ) : (
                        <><WarningCircle size={18} weight="fill" color="#f59e0b" />
                          <span className="text-sm font-bold" style={{ color: '#f59e0b' }}
                                data-testid="2fa-status">Vypnuto</span></>
                    )}
                </div>
                {status.enabled ? (
                    <>
                        <div className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                            Aktivováno: {new Date(status.enabled_at).toLocaleString('cs-CZ')}<br/>
                            Zbývající recovery kódy: <b>{status.recovery_codes_remaining}</b>
                        </div>
                        <div className="text-xs uppercase tracking-[0.2em] font-bold mb-2 mt-4"
                             style={{ color: 'var(--text-secondary)' }}>Deaktivovat 2FA</div>
                        <div className="flex gap-2">
                            <input
                                data-testid="2fa-disable-input"
                                value={disableCode}
                                onChange={(e) => setDisableCode(e.target.value)}
                                placeholder="TOTP kód nebo recovery"
                                className="flex-1 bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono text-sm"
                                style={{ borderColor: 'var(--border-default)' }}
                            />
                            <button data-testid="2fa-disable-btn" onClick={disable}
                                    className="px-4 py-2 rounded-lg border font-bold text-sm
                                              hover:border-[var(--danger)] hover:text-[var(--danger)]"
                                    style={{ borderColor: 'var(--border-default)' }}>
                                Vypnout
                            </button>
                        </div>
                    </>
                ) : !setup ? (
                    <button data-testid="2fa-setup-btn" onClick={startSetup}
                            className="px-4 py-2 rounded-lg font-bold text-sm"
                            style={{ background: 'var(--brand)', color: '#000' }}>
                        <QrCode size={14} className="inline mr-2" /> Aktivovat 2FA
                    </button>
                ) : (
                    <div data-testid="2fa-setup-panel">
                        <div className="text-sm mb-3">
                            <b>1.</b> Naskenujte QR kód v autentikační aplikaci:
                        </div>
                        <div className="flex gap-6 items-start mb-4">
                            <img src={setup.qr_png_base64} alt="2FA QR"
                                 className="w-48 h-48 bg-white p-2 rounded"
                                 data-testid="2fa-qr-img" />
                            <div>
                                <div className="text-xs mb-1"
                                     style={{ color: 'var(--text-secondary)' }}>
                                    Nebo zadejte ručně:
                                </div>
                                <div className="flex items-center gap-2 mb-3">
                                    <code className="bg-[var(--bg-app)] px-2 py-1 rounded text-xs font-mono">
                                        {setup.secret_b32}
                                    </code>
                                    <button onClick={() => copy(setup.secret_b32)}
                                            className="p-1 hover:text-[var(--brand)]">
                                        <Copy size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="text-sm mb-3">
                            <b>2.</b> Zadejte 6místný kód z aplikace pro potvrzení:
                        </div>
                        <div className="flex gap-2">
                            <input
                                data-testid="2fa-verify-input"
                                value={verifyCode}
                                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="123456"
                                className="flex-1 bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono text-2xl text-center tracking-[0.3em]"
                                style={{ borderColor: 'var(--border-default)' }}
                                maxLength={6}
                            />
                            <button data-testid="2fa-verify-btn" onClick={verify}
                                    disabled={verifyCode.length !== 6}
                                    className="px-6 py-2 rounded-lg font-bold text-sm disabled:opacity-30"
                                    style={{ background: 'var(--brand)', color: '#000' }}>
                                <FloppyDisk size={14} className="inline mr-2" /> Potvrdit
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {recoveryCodes && (
                <div className="rounded-xl border p-6 mb-4"
                     data-testid="2fa-recovery-codes"
                     style={{ background: 'rgba(245,158,11,0.1)', borderColor: '#f59e0b' }}>
                    <div className="flex items-center gap-2 mb-3">
                        <KeyReturn size={18} color="#f59e0b" />
                        <h3 className="text-base font-bold" style={{ color: '#f59e0b' }}>
                            Záložní kódy — uložte si je teď!
                        </h3>
                    </div>
                    <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
                        Použijte jeden z nich při ztrátě telefonu. Každý kód funguje pouze jednou.
                        Po opuštění této stránky je už neuvidíte!
                    </p>
                    <div className="grid grid-cols-2 gap-2 font-mono text-sm mb-3">
                        {recoveryCodes.map((c) => (
                            <code key={c} className="bg-[var(--bg-app)] px-3 py-2 rounded">{c}</code>
                        ))}
                    </div>
                    <button onClick={() => copy(recoveryCodes.join('\n'))}
                            data-testid="2fa-copy-recovery"
                            className="px-3 py-2 rounded text-xs border hover:bg-[var(--bg-surface-hover)]"
                            style={{ borderColor: 'var(--border-default)' }}>
                        <Copy size={12} className="inline mr-1" /> Kopírovat všechny
                    </button>
                </div>
            )}
        </div>
    );
};

export default TwoFactorAuth;
